
import React, { useState } from 'react';
import { Invoice, PlanTier } from '../types';
import { api } from '../services/api';

interface InvoiceTableProps {
  invoices: Invoice[];
  onUpdate?: (invoice: Invoice) => void;
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({ invoices, onUpdate }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Invoice | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleEditClick = (invoice: Invoice) => {
    setEditingId(invoice.id);
    setEditForm({ ...invoice });
  };

  const handleCancelClick = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const handleSaveClick = () => {
    if (editForm && onUpdate) {
      onUpdate(editForm);
      setEditingId(null);
      setEditForm(null);
    }
  };

  const handleChange = (field: keyof Invoice, value: any) => {
    if (editForm) {
      setEditForm({ ...editForm, [field]: value });
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'Paid': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Open': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Void': return 'bg-slate-100 text-slate-700 border-slate-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getPlanStyle = (plan: PlanTier) => {
    switch (plan) {
      case PlanTier.ENTERPRISE: return 'text-purple-700 bg-purple-50';
      case PlanTier.PRO: return 'text-indigo-700 bg-indigo-50';
      default: return 'text-slate-700 bg-slate-50';
    }
  };

  const handleDownloadPDF = async (inv: Invoice) => {
    setDownloadingId(inv.id);
    try {
      const blob = await api.invoices.getPdf(inv.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${inv.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
      alert("Failed to download PDF invoice. Please check the API connection.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Invoice No.</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Customer</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Amount</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map((inv) => {
              const isEditing = editingId === inv.id;
              
              return (
                <tr key={inv.id} className={`group transition-colors ${isEditing ? 'bg-indigo-50/30' : 'hover:bg-slate-50'}`}>
                  <td className="px-8 py-5">
                    {isEditing ? (
                      <input 
                        type="text" 
                        value={editForm?.invoiceNumber} 
                        onChange={(e) => handleChange('invoiceNumber', e.target.value)}
                        className="w-full bg-white border border-indigo-200 rounded-lg px-3 py-1 text-xs font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="font-mono text-xs font-bold text-slate-900">{inv.invoiceNumber}</span>
                    )}
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col">
                        <span className="text-sm font-black text-slate-900">{inv.customerName}</span>
                        <span className={`text-[9px] font-black uppercase tracking-tighter px-1.5 py-0.5 rounded-md self-start mt-1 ${getPlanStyle(inv.planTier)}`}>{inv.planTier}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    {isEditing ? (
                      <input 
                        type="date" 
                        value={editForm?.date} 
                        onChange={(e) => handleChange('date', e.target.value)}
                        className="bg-white border border-indigo-200 rounded-lg px-3 py-1 text-xs font-bold text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="text-xs text-slate-500 font-bold">{inv.date}</span>
                    )}
                  </td>
                  <td className="px-8 py-5">
                    {isEditing ? (
                      <select 
                        value={editForm?.status}
                        onChange={(e) => handleChange('status', e.target.value)}
                        className="bg-white border border-indigo-200 rounded-lg px-2 py-1 text-[10px] font-black uppercase tracking-widest text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="Paid">Paid</option>
                        <option value="Open">Open</option>
                        <option value="Void">Void</option>
                        <option value="Uncollectible">Uncollectible</option>
                      </select>
                    ) : (
                      <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusStyle(inv.status)}`}>{inv.status}</span>
                    )}
                  </td>
                  <td className="px-8 py-5 text-right">
                    {isEditing ? (
                      <input 
                        type="number" 
                        value={editForm?.amount} 
                        onChange={(e) => handleChange('amount', parseFloat(e.target.value))}
                        className="w-24 bg-white border border-indigo-200 rounded-lg px-3 py-1 text-xs font-black text-slate-900 text-right focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="font-black text-slate-900">${inv.amount.toLocaleString()}</span>
                    )}
                  </td>
                  <td className="px-8 py-5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {onUpdate && (
                        isEditing ? (
                          <>
                            <button
                              onClick={handleSaveClick}
                              className="p-2 text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors shadow-sm"
                              title="Save Changes"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            </button>
                            <button
                              onClick={handleCancelClick}
                              className="p-2 text-slate-500 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
                              title="Cancel"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleEditClick(inv)}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Edit Invoice"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                          </button>
                        )
                      )}
                      {!isEditing && (
                        <button 
                          onClick={() => handleDownloadPDF(inv)}
                          disabled={downloadingId === inv.id}
                          className="px-3 py-1.5 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {downloadingId === inv.id ? (
                              <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
                            )}
                            PDF
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceTable;
